/**
 * MechanicIncentiveTile — mechanic self-view (iter161)
 * ----------------------------------------------------
 * A big prominent tile the mechanic sees on their home dashboard
 * showing their running incentive bucket balance since the workshop's
 * last reset date.
 *
 * Positive balance → EMERALD, treated as "profit going to your bonus".
 * Negative balance → AMBER (not red — motivation-first, per iter154).
 *
 * Reads GET /api/mechanic/incentive-bucket (self by default).
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Award, TrendingUp, TrendingDown, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtSigned = (n) => {
  const v = Number(n) || 0;
  const sign = v > 0.05 ? '+' : v < -0.05 ? '−' : '';
  return `${sign}${Math.abs(v).toFixed(1)}h`;
};

export default function MechanicIncentiveTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/mechanic/incentive-bucket`,
          { credentials: 'include' },
        );
        if (!res.ok || cancelled) return;
        const j = await res.json();
        setData(j);
      } catch (_e) { /* silent */ } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="p-4" data-testid="mechanic-incentive-tile-loading">
        <p className="text-xs text-slate-500">Loading your bucket…</p>
      </Card>
    );
  }
  if (!data) return null;

  const bucket = Number(data.bucket_total_hours || 0);
  const today = data.today || {};
  const week = data.this_week || {};

  const positive = bucket > 0.05;
  const negative = bucket < -0.05;
  const cardTone =
    positive ? 'border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100/60'
    : negative ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100/60'
    : 'border-slate-200 bg-slate-50';
  const bigTone =
    positive ? 'text-emerald-800' : negative ? 'text-amber-800' : 'text-slate-700';

  return (
    <Card className={`relative overflow-hidden border-2 ${cardTone} p-5`} data-testid="mechanic-incentive-tile">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
            positive ? 'bg-emerald-500 text-white' : negative ? 'bg-amber-500 text-white' : 'bg-slate-400 text-white'
          }`}>
            <Award className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Incentive Bucket
            </p>
            <p className="text-xs text-slate-600">
              Since {data.reset_date}
            </p>
          </div>
        </div>
        {positive && (
          <span className="text-[10px] uppercase tracking-wider font-black bg-emerald-500 text-white px-2 py-1 rounded-full">
            In profit
          </span>
        )}
        {negative && (
          <span className="text-[10px] uppercase tracking-wider font-black bg-amber-500 text-white px-2 py-1 rounded-full">
            Catching up
          </span>
        )}
      </div>

      {/* Big number */}
      <div className="flex items-baseline gap-2">
        <span className={`text-5xl font-black tabular-nums ${bigTone}`} data-testid="mechanic-incentive-bucket-value">
          {fmtSigned(bucket)}
        </span>
        {positive && <TrendingUp className="h-6 w-6 text-emerald-600 self-center" />}
        {negative && <TrendingDown className="h-6 w-6 text-amber-600 self-center" />}
      </div>

      {/* Today + This Week strip */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="bg-white/70 rounded-lg p-2 border border-slate-200/60" data-testid="mechanic-incentive-today">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Today
          </p>
          <p className="text-sm font-black tabular-nums text-slate-800 mt-0.5">
            {Number(today.completed_hours || 0).toFixed(1)}h
            <span className="text-slate-400 font-medium mx-1">/</span>
            <span className="text-slate-600 font-semibold">{Number(today.target_hours || 0).toFixed(1)}h</span>
          </p>
          <p className={`text-[11px] font-bold ${
            (today.delta_hours || 0) > 0.05 ? 'text-emerald-700'
            : (today.delta_hours || 0) < -0.05 ? 'text-amber-700'
            : 'text-slate-500'
          }`}>
            {fmtSigned(today.delta_hours)} today
          </p>
        </div>
        <div className="bg-white/70 rounded-lg p-2 border border-slate-200/60" data-testid="mechanic-incentive-week">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> This week
          </p>
          <p className="text-sm font-black tabular-nums text-slate-800 mt-0.5">
            {Number(week.completed_hours || 0).toFixed(1)}h
            <span className="text-slate-400 font-medium mx-1">/</span>
            <span className="text-slate-600 font-semibold">{Number(week.target_hours || 0).toFixed(1)}h</span>
          </p>
          <p className={`text-[11px] font-bold ${
            (week.delta_hours || 0) > 0.05 ? 'text-emerald-700'
            : (week.delta_hours || 0) < -0.05 ? 'text-amber-700'
            : 'text-slate-500'
          }`}>
            {fmtSigned(week.delta_hours)} this week
          </p>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
        Bucket = daily <strong>completed</strong> minus your daily target
        (<strong>{Number(data.targets?.daily_target_hours || 0).toFixed(1)}h/day</strong>,{' '}
        {Number(data.targets?.weekly_target_hours || 0).toFixed(0)}h/week).
        Positive hours grow your bonus — negative hours eat into it.
      </p>
    </Card>
  );
}
